import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import { Alert, Button, Checkbox, Layout, Modal, Table, Tag, Switch, message } from 'antd';
import Spinner from '../../Spinner/Spinner';
import useSearch from '../../../gql/hooks/useSearch'; 
import useHasError from '../../../hooks/useHasError'; 
import useIsLoading from '../../../hooks/useIsLoading'; 
import SearchReorder from '../SearchReorder/SearchReorder';
import './styles.less';

const SearchQueryMiniSelectedList = ( { type, tableColumns, selectItemsChangeCallback, 
    initialItems = [], disabled = false, onChange, reorder = false, triggerFormItemChangeCallback,
    showType = true, disabledItemsChangeCallback, initialDisabledItems = [], showFilterDisabled = false,
    canReorderCallback, saveReorderCallback, initalSelectedObjects = [], disableReorderCallback,
    revisionLearningObjects, ref } ) => {
    const reorderRef = useRef( null );
    const [ tableData, setTableData ] = useState( [] );
    const [ currentPage, setCurrentPage ] = useState( 1 );
    const [ searchIds, setSeachIds ] = useState( [] ); 
    const [ sortField ] = useState( 'name' ); 
    const [ sortDirection ] = useState( 'ascend' ); 
    const { searchData, searchError, searchLoading, loadSearch } = useSearch( { type } );
    const [ selectedItems, setSelectedItems ] = useState( [] );
    const [ disabledItems, setDisabledItems ] = useState( initialDisabledItems );
    const [ showReorder, setShowReorder ] = useState( false );
    const [ showDisabled, setShowDisabled ] = useState( false );

    const { isLoading } = useIsLoading( { items: [ searchLoading ] } );
    const { hasError } = useHasError( { items: [ searchError ] } );
    
    const sortItems = ( items ) => items.length 
        ? searchIds.map( id => items.find( i => i.id === id ) ).filter( Boolean ) 
        : [];

    const addRevisionToItems = ( items ) => {
        if ( !revisionLearningObjects ) {
            return items;
        }

        const r = items.map( i => {
            return { 
                ...i,
                isInRevision: revisionLearningObjects.includes( i.id )
            };
        } );
        return r;
    };

    useEffect( () => { 
        setTableData( addRevisionToItems( sortItems( searchData ) ) );   
    }, [ searchData ] );
    

    useEffect( () => { 
        setDisabledItems( initialDisabledItems );   
    }, [ initialDisabledItems ] );
    
    useEffect( () => {
        if ( onChange && JSON.stringify( searchIds ) !== JSON.stringify( initialItems ) ) {
            updateListItems( initialItems );
        }
    }, [ initialItems, onChange ] );

    useImperativeHandle( ref, () => ( {
        setDisabledItems: ( items ) => {
            setDisabledItems( items );
        },
        setSelectedItems: ( items ) => {
            setSelectedItems( items );
            selectItemsChangeCallback?.( items );
        },
        removeItems: ( items ) => {
            const newItems = searchIds.filter( ( item ) => !items.includes( item ) );
            updateListItems( newItems );
            return newItems;
        },
        addItems: ( items ) => {
            const newItems = [ ...searchIds, ...items ];
            updateListItems( newItems );
            return newItems;
        },
        moveToBottom: ( item ) => {
            const newItems = [ ...searchIds ];
            const itemIndex = newItems.indexOf( item );
            
            if ( itemIndex !== -1 ) {
                newItems.splice( itemIndex, 1 );
                newItems.push( item );
                updateListItems( newItems );
            }
        }
    } ) );

    const updateListItems = ( items = [] ) => {
        setSeachIds( items );
        setCurrentPage( 1 );    

        if ( items.length === 0 ) {
            setTableData( [] );
            return;
        }   
        
        loadSearch( { 
            searchIds: items,
            searchText: '', 
            currentPage: 1, 
            sortField: sortField, 
            sortDirection: sortDirection,
            limit: 10000
        } );
    };

    const getListItemType = ( item ) => {
        const formatMap = {
            objects: () => item.format,
            courses: () => item.learningObjects?.[ 0 ]?.format,
            categories: () => item.brandName
        };

        const format = formatMap[ type ]?.();
        return format ? <Tag>{ format.toUpperCase() }</Tag> : null;
    };

    const handleSelectCheck = ( event, item ) => {
        const checked = event.target.checked;
        const itemId = item.id;
    
        if ( checked === selectedItems.includes( itemId ) ) {
            return;
        }

        const newSelectedItems = checked 
            ? [ ...selectedItems, itemId ]
            : selectedItems.filter( id => id !== itemId );
    
        setSelectedItems( newSelectedItems );
        selectItemsChangeCallback?.( newSelectedItems );
    };

    const handleEnableToggle = ( event, item ) => {
        const checked = event.target.checked;
        const itemId = item.id;
    
        if ( checked === disabledItems.includes( itemId ) ) {
            return;
        }

        const newItems = checked 
            ? [ ...disabledItems, itemId ]
            : disabledItems.filter( id => id !== itemId );
    
        setDisabledItems( newItems );
        disabledItemsChangeCallback?.( newItems, itemId );
    };

    const renderFooter = () => {
        return disabled ? <span>&nbsp;</span> : <span>{ `${ selectedItems.length } Selected` }</span>;
    };

    const itemHasRevision = ( item ) => 
        !revisionLearningObjects ? true : item.isInRevision;

    // Add the filter and render for name column
    tableColumns.forEach( item => {
        if ( item.sortKey === 'name' ) {
            item.render = ( name, i ) => {
                return (
                    <div className="flex-container-name-column">
                        
                        { /* Enabled items */ }
                        { !disabled && !disabledItems.includes( i.id ) &&
                            <>
                                <div className="left-name-column">

                                    { !revisionLearningObjects &&
                                        <>
                                            <Checkbox 
                                                checked={ selectedItems.includes( i.id ) } 
                                                onChange={ ( e ) => handleSelectCheck( e, i ) } 
                                            />
                                            <span style={ { marginLeft: '20px' } }>&nbsp;</span>
                                        </>
                                    }
                                    
                                    { revisionLearningObjects &&
                                        <>
                                            { !itemHasRevision( i ) && 
                                                <Checkbox 
                                                    checked={ selectedItems.includes( i.id ) } 
                                                    onChange={ ( e ) => handleSelectCheck( e, i ) } 
                                                />
                                            }

                                            { itemHasRevision( i ) &&
                                                <span style={ { marginLeft: '20px' } }>&nbsp;</span>
                                            }
                                        </>
                                    }
                                
                                    { name }
                                </div>

                                { showType &&
                                    <div className="right-name-column">
                                        { getListItemType( i ) }
                                    </div>
                                } 
                            </>
                        }

                        { ( disabledItems.includes( i.id ) || disabled ) &&
                            <>
                                <div className="left-name-column disabled">
                                    { name }
                                </div>

                                { showType &&
                                    <div className="right-name-column disabled">
                                        { getListItemType( i ) }
                                    </div>
                                }
                            </>                               
                        }
                    </div>
                );
            };
        }
        if ( item.sortKey === 'disabled' ) {
            item.render = ( name, i ) => {
                return initalSelectedObjects.includes( i.id ) && itemHasRevision( i ) ? 
                    <Checkbox 
                        disabled={ disabled }
                        checked={ disabledItems.includes( i.id ) }
                        onChange={ ( e ) => handleEnableToggle( e, i ) } 
                    /> 
                    : null;
            };
        }
    } );

    /*************************************
     * ANCHOR Event handlers
     *************************************/

    const handlePageChange = ( pageNum ) => {
        setCurrentPage( pageNum );
    };

    const handleReorderSaveClick = async () => { 
        const items = reorderRef.current.getItems().concat( disabledItems );
        
        if ( saveReorderCallback ) {
            await saveReorderCallback( items );
        }
        
        setShowReorder( false );
        updateListItems( items );
        triggerFormItemChangeCallback( items );
    };

    const handleReorderCancelClick = () => {
        setShowReorder( false );
    };

    const handleReorderClick = () => {
        if ( canReorderCallback ) {
            if ( canReorderCallback() ) {
                setShowReorder( true );
            } else {
                message.info( 'You must save your changes before reordering them.', 10 );
            }
        } else {
            setShowReorder( true );
        }
    };

    const handleToggleDisabledView = ( value ) => {
        setShowDisabled( value );
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return (
        <Layout className={ `search-query-mini-selected-list-page ${ disabled ? 'disabled' : '' }` }>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
            
            { /* Main Content */ }
            { !isLoading &&
                <div className="search-query-content">
                    
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            style={ { margin: '10px' } }
                            type="error"
                            message="Error"
                            description={
                                <>
                                    <p>An unknown error occurred. Please try again later.</p>
                                    <Button
                                        onClick={ () => loadSearch( { 
                                            searchText: '',
                                            searchIds,
                                            currentPage,
                                            sortField,
                                            sortDirection,
                                            limit: 10000
                                        } ) }
                                    >
                                        Reload
                                    </Button>
                                </>
                            }
                        />
                    }
                    
                    { !hasError && 
                        <> 
                            { reorder && 
                                <>
                                    { showFilterDisabled &&
                                        <Switch 
                                            className="disable-button"
                                            checkedChildren="Hide Disabled" 
                                            unCheckedChildren="Show Disabled" 
                                            onChange={ handleToggleDisabledView }
                                        />
                                    }

                                    <Button 
                                        className="reorder-button"
                                        disabled={ disabled || showReorder || searchIds.length < 2 || ( disableReorderCallback ? disableReorderCallback() : false ) }
                                        onClick={ handleReorderClick }
                                    >
                                        Reorder
                                    </Button>

                                    { showReorder &&
                                        <Modal 
                                            title="Drag to Reorder" 
                                            open={ showReorder } 
                                            onOk={ handleReorderSaveClick }
                                            onCancel={ handleReorderCancelClick }
                                            okText= { saveReorderCallback ? 'Save' :  'Ok' }
                                            bodyStyle={ { 
                                                height: '600px', 
                                                overflowX: 'hidden',
                                                overflowY: 'scroll' 
                                            } }
                                        >
                                            <SearchReorder 
                                                ref={ reorderRef }
                                                type={ type }
                                                ids={ searchIds }
                                                items={ tableData.filter( i => !disabledItems.includes ( i.id ) ) }
                                            />
                                        </Modal>    
                                    }
                                </>
                            }

                            { /* Main table */ }
                            <Table 
                                style={ reorder ?  { marginTop: '0px' } : { marginTop: '41px' } }
                                loading= { searchLoading }
                                columns={ tableColumns }
                                dataSource={ showDisabled ? 
                                    tableData.filter( i => disabledItems.includes ( i.id ) ) : 
                                    tableData.filter( i => !disabledItems.includes ( i.id ) ) }
                                size="small"
                                pagination={  disabled ? false : {
                                    onChange: handlePageChange, 
                                    defaultCurrent: currentPage, 
                                    position: [ 'bottomRight' ],
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                                footer={ renderFooter }
                                locale={ {
                                    emptyText: <div style={ { minHeight: '308px' } }></div>,
                                } }
                            />
                        </>
                    }
                </div>
            }
        </Layout>
    );
};

export default SearchQueryMiniSelectedList;
